import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import { AvailabilityForSidebar } from 'api/services/availability/availability-service.dto';
import { DateTime } from 'luxon';
import { useGetWorkerAvailability } from './use-get-worker-availability';
import { SelectedDateWithAvailability } from '../wizard/availability-sidebar';
import { SegmentsForEdit, TimeSlotFormContext } from '../wizard/time-slot.types';

export const useGetAdditionalAvailabilities = ({
  selectedDates,
  setSelectedDates,
  monthAvailabilityQuery,
  selectedFirstMonth,
}: {
  selectedDates: SelectedDateWithAvailability[];
  setSelectedDates: React.Dispatch<React.SetStateAction<SelectedDateWithAvailability[]>>;
  monthAvailabilityQuery: ReturnType<typeof useGetWorkerAvailability>;
  selectedFirstMonth: DateTime;
}) => {
  const { watch } = useFormContext<TimeSlotFormContext>();
  const selectedWorkerId = watch('workerSelect');

  const mapQuerySegmentToSegmentForEdit = (segment: AvailabilityForSidebar) => ({
    segmentId: segment.segmentId,
    utcStartDateTime: DateTime.fromISO(segment.startTime).toFormat('T'),
    utcEndDateTime: DateTime.fromISO(segment.endTime).toFormat('T'),
  });
  const mapQuerySegmentToSegmentForCalendarState = (segment: AvailabilityForSidebar) => ({
    segmentId: segment.segmentId,
    startTime: DateTime.fromISO(segment.startTime),
    endTime: DateTime.fromISO(segment.endTime),
  });
  const mapRelatedAvailabilityToSegmentForEdit = (availability: {
    segmentId: string;
    startTime: DateTime;
    endTime: DateTime;
  }) => ({
    segmentId: availability.segmentId,
    utcStartDateTime: availability.startTime.toFormat('T'),
    utcEndDateTime: availability.endTime.toFormat('T'),
  });
  const retrieveSegmentsThatAreNotInView = (
    alreadySelectedDates: SelectedDateWithAvailability[],
    relevantSegments: SegmentsForEdit[]
  ) => {
    return alreadySelectedDates
      .flatMap((item) => item.relatedAvailability?.map(mapRelatedAvailabilityToSegmentForEdit))
      .filter((item) => !!item)
      .filter(
        (segment) =>
          !relevantSegments.some(
            (relevantSegment) => relevantSegment.segmentId === segment.segmentId
          )
      );
  };

  const availabilitiesForAllSelectedDays: SegmentsForEdit[] | undefined = useMemo(() => {
    let relevantSegments: SegmentsForEdit[] = [];
    selectedDates.forEach((selectedDate, index) => {
      const formattedDateKey = selectedDate.date.toFormat('dd-MM-yyyy');
      if (
        monthAvailabilityQuery.status === 'success' &&
        monthAvailabilityQuery.data.availabilities[formattedDateKey]?.availableSegments
      ) {
        relevantSegments = relevantSegments.concat(
          monthAvailabilityQuery.data?.availabilities[formattedDateKey].availableSegments.map(
            mapQuerySegmentToSegmentForEdit
          )
        );
        setSelectedDates((prev) =>
          prev.map((date, i) =>
            i === index
              ? {
                  ...date,
                  relatedAvailability: monthAvailabilityQuery.data.availabilities[
                    formattedDateKey
                  ].availableSegments.map(mapQuerySegmentToSegmentForCalendarState),
                }
              : date
          )
        );
      }
    });

    const relevantSegmentsNotAlreadyInView = retrieveSegmentsThatAreNotInView(
      selectedDates,
      relevantSegments
    );
    return relevantSegmentsNotAlreadyInView.concat(relevantSegments);
  }, [monthAvailabilityQuery.data, selectedFirstMonth, selectedDates.length, selectedWorkerId]);

  return { availabilitiesForAllSelectedDays };
};
